// 060 — Lista de Verificación Cirugía Segura (Fase 7, Tanda 8, ver PLAN.md). Transcripción campo por campo del PDF
// oficial del Anexo 1 MSP: hoja idx 216 (footer `SNS-MSP/HCU-form.060/2021`). Informe de brechas en
// `docs/hce-fase7/brechas-121-057-057A-060.md`.
//
// Cada fila SI/NO es un select con "sin responder" como estado inicial: en una lista de verificación no se puede
// confundir "no se marcó" con "NO", ni "NO" con "NO PROCEDE"/"NO APLICA" (el Questionnaire anterior usaba booleanos
// y perdía las tres distinciones). Las filas del papel sin separadores se leyeron como una casilla SI y una NO por
// fila con exclusión mutua. Sin FHIR: no hay consumidor demostrable; queda como `Par[]` en el PDF firmado.
import { paresDeCampos, valoresVacios, type CampoSimple, type OpcionCampo, type Valores } from './camposSimplesDatos';
import type { Par } from './index';

const SI: OpcionCampo = { valor: 'si', etiqueta: 'Sí' };
const NO: OpcionCampo = { valor: 'no', etiqueta: 'No' };
const NO_PROCEDE: OpcionCampo = { valor: 'noprocede', etiqueta: 'No procede' };
const NO_APLICA: OpcionCampo = { valor: 'noaplica', etiqueta: 'No aplica' };

const SI_NO = [SI, NO];
const SI_NO_PROCEDE = [SI, NO, NO_PROCEDE];
const fila = (clave: string, etiqueta: string, opciones: readonly OpcionCampo[] = SI_NO): CampoSimple => ({ clave, etiqueta, tipo: 'select', opciones });
const texto = (clave: string, etiqueta: string, tipo?: CampoSimple['tipo']): CampoSimple => ({ clave, etiqueta, ...(tipo ? { tipo } : {}) });

// ---------- B. Datos de la cirugía ----------

export const CAMPOS_CIRUGIA_060: readonly CampoSimple[] = [
  texto('fecha', 'Fecha', 'fecha'),
  texto('procedimientoPropuesto', 'Procedimiento propuesto'),
  texto('quirofano', 'Quirófano'),
];

// ---------- C. Entrada (antes de la inducción de la anestesia) ----------

export const CAMPOS_ENTRADA_CONFIRMADO_060: readonly CampoSimple[] = [
  fila('confirmoIdentidad', 'El paciente ha confirmado — su identidad'),
  fila('confirmoSitio', 'El paciente ha confirmado — el sitio quirúrgico'),
  fila('confirmoProcedimiento', 'El paciente ha confirmado — el procedimiento'),
  fila('confirmoConsentimiento', 'El paciente ha confirmado — su consentimiento verbal y escrito'),
];

export const CAMPOS_ENTRADA_060: readonly CampoSimple[] = [
  fila('demarcacion', 'Demarcación del sitio quirúrgico', SI_NO_PROCEDE),
  fila('equipoIntubacion', 'Control formal del instrumental anestésico, medicación y riesgo anestésico — equipo de intubación'),
  fila('equipoAspiracion', 'Control formal del instrumental anestésico — equipo de aspiración de la vía aérea'),
  fila('ventilacionOxigeno', 'Control formal del instrumental anestésico — sistema de ventilación: oxígeno'),
  fila('ventilacionInhalados', 'Control formal del instrumental anestésico — sistema de ventilación: fármacos inhalados'),
  fila('ventilacionMedicacion', 'Control formal del instrumental anestésico — sistema de ventilación: medicación'),
  fila('pulsioximetro', 'Pulsioxímetro colocado en el paciente y funcionando'),
  fila('capnografo', 'Capnógrafo colocado y funcionando', SI_NO_PROCEDE),
  fila('alergias', 'El paciente tiene alergias conocidas'),
  texto('alergiasCuales', 'Alergias conocidas — cuáles'),
  fila('viaAerea', 'Vía aérea difícil / riesgo de aspiración', [
    { valor: 'si', etiqueta: 'Sí, y hay instrumental y equipos disponibles' },
    NO,
  ]),
  fila('hemorragia', 'Riesgo de hemorragia > 500 ml (7 ml/kg en niños)', [
    { valor: 'si', etiqueta: 'Sí, y se ha previsto la disponibilidad de acceso intravenoso y líquidos adecuados' },
    NO,
  ]),
  fila('reservaHemoderivados', 'Se ha confirmado la reserva de hemoderivados con el laboratorio', [SI, NO, NO_APLICA]),
];

// ---------- D. Pausa quirúrgica (antes de la incisión cutánea) ----------

export const CAMPOS_PAUSA_060: readonly CampoSimple[] = [
  fila('equipoPresentado', 'Todos los miembros del equipo se han presentado por su nombre y función'),
  fila('confirmaIdentidad', 'El responsable de la lista de chequeo confirma verbalmente con el equipo — identidad del paciente'),
  fila('confirmaSitio', 'El responsable de la lista de chequeo confirma verbalmente con el equipo — sitio quirúrgico'),
  fila('confirmaProcedimiento', 'El responsable de la lista de chequeo confirma verbalmente con el equipo — procedimiento (lateralidad)'),
  fila('cirujanoDuracion', 'Previsión de eventos críticos, el cirujano expresa — duración del procedimiento'),
  fila('cirujanoSangre', 'Previsión de eventos críticos, el cirujano expresa — pérdida prevista de sangre'),
  fila('anestesiologoProblema', 'El anestesiólogo expresa algún problema específico'),
  fila('enfermeriaEsterilidad', 'Enfermería y/o instrumentación revisa — esterilidad (con resultado de indicadores e integradores químicos internos y externos)'),
  fila('enfermeriaRecuentoInicial', 'Enfermería y/o instrumentación revisa — recuento inicial de material blanco e instrumental quirúrgico'),
  fila('enfermeriaDudas', 'Enfermería y/o instrumentación revisa — dudas o problemas relacionados con el instrumental y equipos'),
  fila('profilaxis', 'Se ha administrado profilaxis antibiótica en los últimos 60 minutos', SI_NO_PROCEDE),
  fila('imagenes', 'Dispone de imágenes diagnósticas esenciales para el procedimiento quirúrgico', SI_NO_PROCEDE),
];

// ---------- E. Salida (antes de que el paciente salga del quirófano) ----------

export const CAMPOS_SALIDA_060: readonly CampoSimple[] = [
  fila('recuentoFinal', 'El responsable de la lista de chequeo confirma verbalmente con el equipo quirúrgico — el recuento FINAL de material blanco e instrumental quirúrgico (previo al cierre) está completo'),
  fila('empaquetar', 'Hubo necesidad de empaquetar al paciente'),
  texto('compresas', 'Número de compresas', 'numero'),
  texto('procedimientoRealizado', 'Nombre del procedimiento realizado', 'area'),
  fila('herida', 'Clasificación de la herida', [
    { valor: 'limpia', etiqueta: 'Limpia' },
    { valor: 'limpiacontaminada', etiqueta: 'Limpia-contaminada' },
    { valor: 'contaminada', etiqueta: 'Contaminada' },
    { valor: 'sucia', etiqueta: 'Sucia' },
  ]),
  fila('tomaMuestras', 'Toma de muestras'),
  fila('etiquetado', 'Etiquetado de las muestras (nombres y apellidos completos del paciente, historia clínica, fecha)'),
  { clave: 'muestraCitoquimico', etiqueta: 'Tipo de muestra a enviar — citoquímico', tipo: 'casilla' },
  texto('citoquimicoNumero', 'Citoquímico — No.', 'numero'),
  texto('citoquimicoNombre', 'Citoquímico — nombre'),
  { clave: 'muestraCultivos', etiqueta: 'Tipo de muestra a enviar — cultivos', tipo: 'casilla' },
  texto('cultivosNumero', 'Cultivos — No.', 'numero'),
  texto('cultivosNombre', 'Cultivos — nombre'),
  { clave: 'muestraAnatomopatologico', etiqueta: 'Tipo de muestra a enviar — anatomopatológico', tipo: 'casilla' },
  texto('anatomopatologicoNumero', 'Anatomopatológico — No.', 'numero'),
  texto('anatomopatologicoNombre', 'Anatomopatológico — nombre'),
  texto('muestraOtros', 'Tipo de muestra a enviar — otros', 'area'),
  fila('problemasInstrumental', 'Hay problemas que resolver, relacionados con el instrumental y los equipos'),
  texto('problemasCuales', 'Problemas del instrumental y equipos — cuáles', 'area'),
  fila('recuperacion', 'El cirujano, el anestesiólogo y el personal de enfermería revisan los principales aspectos de la recuperación del paciente'),
];

// ---------- F. Datos de los profesionales responsables (la firma y el sello son manuscritos) ----------

export const CAMPOS_RESPONSABLES_060: readonly CampoSimple[] = [
  texto('listaNombre', 'Persona responsable de la lista de verificación — nombre completo'),
  texto('cirujanoNombre', 'Cirujano — nombre completo'),
  texto('anestesiologoNombre', 'Anestesiólogo — nombre completo'),
];

export interface Estado060 {
  readonly cirugia: Valores;
  readonly confirmado: Valores;
  readonly entrada: Valores;
  readonly pausa: Valores;
  readonly salida: Valores;
  readonly responsables: Valores;
}

export function estadoInicial060(): Estado060 {
  return {
    cirugia: valoresVacios(CAMPOS_CIRUGIA_060),
    confirmado: valoresVacios(CAMPOS_ENTRADA_CONFIRMADO_060),
    entrada: valoresVacios(CAMPOS_ENTRADA_060),
    pausa: valoresVacios(CAMPOS_PAUSA_060),
    salida: valoresVacios(CAMPOS_SALIDA_060),
    responsables: valoresVacios(CAMPOS_RESPONSABLES_060),
  };
}

/**
 * Ítems de la lista (todas las filas SI/NO/NO PROCEDE...) que quedaron sin responder. El instructivo no obliga a llenar cada
 * fila (algunas dependen del caso), así que NO bloquea: solo se avisa cuántos faltan antes de firmar.
 */
export function itemsSinResponder060(e: Estado060): string[] {
  const grupos: readonly [readonly CampoSimple[], Valores][] = [
    [CAMPOS_ENTRADA_CONFIRMADO_060, e.confirmado],
    [CAMPOS_ENTRADA_060, e.entrada],
    [CAMPOS_PAUSA_060, e.pausa],
    [CAMPOS_SALIDA_060, e.salida],
  ];
  return grupos.flatMap(([campos, valores]) => campos.filter((c) => c.tipo === 'select' && (valores[c.clave] ?? '').trim() === '').map((c) => c.etiqueta));
}

export function paresDe060(e: Estado060): Par[] {
  return [
    ...paresDeCampos(CAMPOS_CIRUGIA_060, e.cirugia),
    ...paresDeCampos(CAMPOS_ENTRADA_CONFIRMADO_060, e.confirmado, 'Entrada — '),
    ...paresDeCampos(CAMPOS_ENTRADA_060, e.entrada, 'Entrada — '),
    ...paresDeCampos(CAMPOS_PAUSA_060, e.pausa, 'Pausa quirúrgica — '),
    ...paresDeCampos(CAMPOS_SALIDA_060, e.salida, 'Salida — '),
    ...paresDeCampos(CAMPOS_RESPONSABLES_060, e.responsables),
  ].filter((p) => p.respuesta.trim() !== '');
}
